import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  List, ListItem, ListItemText, Divider, TextField, CircularProgress,
  Tabs, Tab, Alert, IconButton, Stack, Tooltip, Checkbox
} from '@mui/material';
import { Check, Close, OpenInNew, Receipt, Visibility, Payment, Print, LocalShipping, ContentCopy } from '@mui/icons-material';
import { Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import toast from 'react-hot-toast';
import HistoryIcon from '@mui/icons-material/History';
import {
  getOrdersAll, getOrderDetails,
  approveOrder, rejectOrder, markOrderPaid, markBhManual,
  // Faz A: state machine endpoint'leri (preparing/shipped/delivered geçişleri)
  getOrderFullHistory
} from '../services/api';
import OrderStateActions from '../components/OrderStateActions';
// Faz BE (2026-05-10): Yeni sipariş hızlı oluşturma dialog
import QuickCreateOrderDialog from '../components/QuickCreateOrderDialog';
// Faz BF (2026-05-10): Müşteri mini 360 drawer
import CustomerMiniDrawer from '../components/CustomerMiniDrawer';
// Faz BL (2026-05-10): WhatsApp ile sipariş özeti paylaş
import WhatsAppShareDialog from '../components/WhatsAppShareDialog';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

// Note 6 — timeline event türü → renk + emoji
const EVENT_STYLE = {
  order_created:    { color: '#1565C0', icon: '🆕' },
  order_confirmed:  { color: '#2E7D32', icon: '✅' },
  audit:            { color: '#6B7280', icon: '📝' },
  push:             { color: '#7C3AED', icon: '🔔' },
  shipment:         { color: '#0288D1', icon: '🚚' },
  shipment_update:  { color: '#0277BD', icon: '📦' },
  efatura:          { color: '#2E7D32', icon: '🧾' }
};

// İş durumu chip'leri
const STATUS = {
  pending:               { label: 'Bekliyor (Havale)',     color: 'default'  },
  paid_pending_approval: { label: 'Ödeme Alındı',          color: 'warning'  },
  confirmed:             { label: 'Onaylandı',             color: 'success'  },
  shipped:               { label: 'Kargoda',               color: 'primary'  },
  delivered:             { label: 'Teslim Edildi',         color: 'success'  },
  cancelled:             { label: 'İptal',                 color: 'error'    },
  rejected:              { label: 'Reddedildi',            color: 'error'    },
  failed:                { label: 'Ödeme Başarısız',       color: 'error'    }
};

// BH durum chip'leri (5d v2)
const BH_STATUS = {
  none:              { label: '⚪ BH Yok',                  color: 'default' },
  draft:             { label: '🟡 BH Taslak',               color: 'warning' },
  manual_completed:  { label: '🟢 Fatura Kesildi',          color: 'success' },
  failed:            { label: '🔴 BH Hata',                 color: 'error'   },
  cancelled:         { label: '⚫ BH İptal',                 color: 'default' }
};

// Tab tanımları — backend filtresi yerine frontend filter (getOrdersAll tek çağrı)
const TABS = [
  { key: 'pending_approval', label: 'Onay Bekliyor', match: o => ['pending', 'paid_pending_approval'].includes(o.status) },
  { key: 'confirmed',        label: 'Onaylı',        match: o => o.status === 'confirmed' },
  { key: 'shipped',          label: 'Kargo',         match: o => o.status === 'shipped' },
  { key: 'delivered',        label: 'Tamamlandı',    match: o => o.status === 'delivered' },
  { key: 'cancelled_rejected', label: 'İptal/Red',   match: o => ['cancelled', 'rejected', 'failed'].includes(o.status) },
  { key: 'all',              label: 'Hepsi',         match: () => true }
];

// İş kuralı: BH paneli linkleri (kullanıcı doğrulayacak)
const BH_PANEL_INVOICE_NEW = 'https://app.bizimhesap.com/Invoice/Add';
const BH_PANEL_INVOICE_VIEW = (guid) => guid ? `https://bizimhesap.com/web/ngn/doc/printout?id=${guid}` : null;

function uuid() {
  // RFC4122 v4 - basit, kriptografik degil (idempotency_key icin yeterli)
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    var r = Math.random() * 16 | 0;
    return (c === 'x' ? r : ((r & 0x3) | 0x8)).toString(16);
  });
}

// Faz BM (2026-05-10): Detay modal'da ortak kart bileşeni
function DetailCard({ icon, title, body }) {
  return (
    <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 1.5, border: '1px solid #e2e8f0', minHeight: 110 }}>
      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
        {icon} {title}
      </Typography>
      <Box sx={{ mt: 0.75 }}>{body}</Box>
    </Box>
  );
}

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0); // 0 = Onay Bekliyor (default)

  // Detay modal
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailOrder, setDetailOrder] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Note 6 — Tam geçmiş modal (audit + push + shipments + efatura timeline)
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const openHistory = async (orderId) => {
    setHistoryOpen(true);
    setHistoryLoading(true);
    setHistoryData(null);
    try {
      const r = await getOrderFullHistory(orderId);
      setHistoryData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Geçmiş yüklenemedi');
    } finally { setHistoryLoading(false); }
  };

  // Onayla dialog
  const [approveDialog, setApproveDialog] = useState({ open: false, order: null, busy: false });

  // Reddet dialog
  const [rejectDialog, setRejectDialog] = useState({ open: false, order: null, reason: '', busy: false });

  // Manuel BH fatura dialog
  const [bhManualDialog, setBhManualDialog] = useState({
    open: false, order: null, guid: '', invoice_no: '', note: '', busy: false
  });

  // Faz BE (2026-05-10): Yeni sipariş quick-create dialog
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);

  // Faz BK (2026-05-10): Sipariş kopyala — prefillOrderId set edilirse dialog kopyala modunda açılır
  const [copyOrderId, setCopyOrderId] = useState(null);
  const openCopyOrder = (orderId) => { setCopyOrderId(orderId); setQuickCreateOpen(true); };

  // Faz BL (2026-05-10): WhatsApp share dialog
  const [waShareOrderId, setWaShareOrderId] = useState(null);

  // Faz BF (2026-05-10): Müşteri mini 360 drawer
  const [customerDrawer, setCustomerDrawer] = useState({ open: false, userId: null });
  const openCustomerDrawer = (userId) => setCustomerDrawer({ open: true, userId });

  // Faz AZ (2026-05-10): Toplu seçim — sipariş id'leri Set olarak tutulur,
  // tab değişince temizlenir (bir tab'taki seçim diğer tab'a karışmasın diye)
  const [selected, setSelected] = useState(new Set());
  useEffect(() => { setSelected(new Set()); }, [activeTab]);
  const toggleSelect = (id) => setSelected(prev => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const toggleSelectAll = (rows) => setSelected(prev => {
    const ids = rows.map(r => r.id);
    const allSelected = ids.every(id => prev.has(id));
    const n = new Set(prev);
    if (allSelected) ids.forEach(id => n.delete(id));
    else ids.forEach(id => n.add(id));
    return n;
  });
  // Faz BA (2026-05-10): Toplu onaylama dialog state
  const [bulkApproveDialog, setBulkApproveDialog] = useState({ open: false, busy: false, results: null });
  const openBulkApproveDialog = () => {
    if (selected.size === 0) { toast.error('Önce sipariş seç'); return; }
    if (selected.size > 50) { toast.error('En fazla 50 sipariş'); return; }
    // Sadece onaylanabilir status'taki seçili sipariş varsa devam et
    const approvable = Array.from(selected).filter(id => {
      const o = orders.find(x => x.id === id);
      return o && ['pending', 'paid_pending_approval'].includes(o.status);
    });
    if (approvable.length === 0) {
      toast.error('Seçili siparişler arasında onaylanabilir (pending/paid_pending_approval) sipariş yok');
      return;
    }
    setBulkApproveDialog({ open: true, busy: false, results: null, approvableIds: approvable });
  };
  const doBulkApprove = async () => {
    setBulkApproveDialog(d => ({ ...d, busy: true }));
    try {
      const ids = bulkApproveDialog.approvableIds;
      const idempotency_keys = {};
      ids.forEach(id => { idempotency_keys[String(id)] = uuid(); });

      const token = localStorage.getItem('token');
      const res = await fetch('https://api.miksanplastik.com.tr/api/admin/orders/bulk-approve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ids, idempotency_keys })
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error('Toplu onay hatası: ' + (data.error || res.status));
        setBulkApproveDialog(d => ({ ...d, busy: false }));
        return;
      }
      toast.success(`${data.success_count}/${data.total} sipariş onaylandı (${data.bh_failed_count} BH hatası)`,
                    { duration: 6000 });
      setBulkApproveDialog(d => ({ ...d, busy: false, results: data }));
      setSelected(new Set());
      await load();
    } catch (e) {
      toast.error('Toplu onay başarısız: ' + e.message);
      setBulkApproveDialog(d => ({ ...d, busy: false }));
    }
  };

  // Faz BC (2026-05-10): Toplu kargoya ver dialog state
  const [bulkShipDialog, setBulkShipDialog] = useState({ open: false, busy: false, results: null, carrier: 'aras', notes: '' });
  const openBulkShipDialog = () => {
    if (selected.size === 0) { toast.error('Önce sipariş seç'); return; }
    if (selected.size > 50) { toast.error('En fazla 50 sipariş'); return; }
    const shippable = Array.from(selected).filter(id => {
      const o = orders.find(x => x.id === id);
      return o && ['confirmed', 'preparing'].includes(o.status);
    });
    if (shippable.length === 0) {
      toast.error('Seçili siparişler arasında kargoya alınabilir (confirmed/preparing) sipariş yok');
      return;
    }
    setBulkShipDialog({ open: true, busy: false, results: null, carrier: 'aras', notes: '', shippableIds: shippable });
  };
  const doBulkShip = async () => {
    setBulkShipDialog(d => ({ ...d, busy: true }));
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('https://api.miksanplastik.com.tr/api/admin/kargo/shipments/bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ids: bulkShipDialog.shippableIds,
          carrier: bulkShipDialog.carrier,
          notes: bulkShipDialog.notes || null
        })
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error('Toplu kargoya hata: ' + (data.error || res.status));
        setBulkShipDialog(d => ({ ...d, busy: false }));
        return;
      }
      toast.success(`${data.success_count}/${data.total} sipariş kargoya verildi${data.is_mock ? ' (mock AWB)' : ''}`,
                    { duration: 6000 });
      setBulkShipDialog(d => ({ ...d, busy: false, results: data }));
      setSelected(new Set());
      await load();
    } catch (e) {
      toast.error('Toplu kargo başarısız: ' + e.message);
      setBulkShipDialog(d => ({ ...d, busy: false }));
    }
  };

  const openBulkPrint = async () => {
    if (selected.size === 0) { toast.error('Önce sipariş seç'); return; }
    if (selected.size > 50) { toast.error('En fazla 50 sipariş — fazlasını kaldır'); return; }
    try {
      const token = localStorage.getItem('token');
      const ids = Array.from(selected).join(',');
      const res = await fetch(
        `https://api.miksanplastik.com.tr/api/admin/orders/print-labels?ids=${ids}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) { toast.error('Toplu etiket alınamadı: ' + res.status); return; }
      const html = await res.text();
      const blob = new Blob([html], { type: 'text/html' });
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (e) {
      toast.error('Toplu etiket açılamadı: ' + e.message);
    }
  };

  const load = useCallback(() => {
    setLoading(true);
    return getOrdersAll()
      .then(r => setOrders(Array.isArray(r.data) ? r.data : []))
      .catch(() => toast.error('Siparişler yüklenemedi'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Tab sayımları
  const tabCounts = useMemo(() => {
    return TABS.map(t => orders.filter(t.match).length);
  }, [orders]);

  const filteredOrders = useMemo(() => {
    return orders.filter(TABS[activeTab].match);
  }, [orders, activeTab]);

  // Detay modal aç
  const openDetail = async (orderId) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailOrder(null);
    try {
      const r = await getOrderDetails(orderId);
      setDetailOrder(r.data);
    } catch {
      toast.error('Detay yüklenemedi');
    } finally {
      setDetailLoading(false);
    }
  };

  // ONAYLA akışı
  // Faz BB: dialog açıldığında müşteri risk profili fetch — banner için
  const [riskProfile, setRiskProfile] = useState(null);
  const [riskLoading, setRiskLoading] = useState(false);
  const openApproveDialog = async (order) => {
    setApproveDialog({ open: true, order, busy: false });
    setRiskProfile(null);
    if (order.user_id) {
      setRiskLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(
          `https://api.miksanplastik.com.tr/api/admin/users/${order.user_id}/risk-profile?for_order_id=${order.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.ok) setRiskProfile(await res.json());
      } catch (_) { /* silent — dialog yine kullanılabilir */ }
      finally { setRiskLoading(false); }
    }
  };
  const closeApproveDialog = () => { setApproveDialog({ open: false, order: null, busy: false }); setRiskProfile(null); };
  const doApprove = async () => {
    if (!approveDialog.order || approveDialog.busy) return;
    setApproveDialog(d => ({ ...d, busy: true }));
    const orderId = approveDialog.order.id;
    const key = uuid();
    try {
      const r = await approveOrder(orderId, key);
      const data = r.data || {};
      if (data.bh_failed) {
        toast.error(`Sipariş onaylandı ancak BH hatası: ${data.bh_error || 'bilinmiyor'}. TASLAK manuel kesilebilir.`, { duration: 6000 });
      } else {
        toast.success('Sipariş onaylandı, BH\'de TASLAK fatura oluşturuldu', { duration: 4000 });
      }
      await load();
      closeApproveDialog();
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Bilinmeyen hata';
      toast.error('Onay hatası: ' + msg, { duration: 6000 });
      setApproveDialog(d => ({ ...d, busy: false }));
    }
  };

  // REDDET akışı
  const openRejectDialog = (order) => setRejectDialog({ open: true, order, reason: '', busy: false });
  const closeRejectDialog = () => setRejectDialog({ open: false, order: null, reason: '', busy: false });
  const doReject = async () => {
    if (!rejectDialog.order || rejectDialog.busy) return;
    const reason = (rejectDialog.reason || '').trim();
    if (reason.length < 10) {
      toast.error('Red sebebi en az 10 karakter olmalı');
      return;
    }
    setRejectDialog(d => ({ ...d, busy: true }));
    const orderId = rejectDialog.order.id;
    try {
      const r = await rejectOrder(orderId, reason, uuid());
      const data = r.data || {};
      let msg = 'Sipariş reddedildi';
      if (data.bh_cancel_attempted && !data.bh_cancel_ok) {
        msg += ' (BH iptal başarısız: ' + (data.bh_cancel_error || 'manual kontrol gerekli') + ')';
      }
      if (data.payment_refund_required) {
        msg += ' — ' + data.payment_refund_required;
      }
      toast.success(msg, { duration: 7000 });
      await load();
      closeRejectDialog();
    } catch (err) {
      const m = err.response?.data?.error || err.message || 'Hata';
      toast.error('Red hatası: ' + m);
      setRejectDialog(d => ({ ...d, busy: false }));
    }
  };

  // Havale ödeme aldı
  const doMarkPaid = async (order) => {
    if (!window.confirm(`#${order.id} havale ödemesi alındı olarak işaretlensin mi?`)) return;
    try {
      await markOrderPaid(order.id);
      toast.success('Ödeme alındı, "Onayla" butonu ile siparişi onaylayabilirsiniz');
      await load();
    } catch (err) {
      toast.error('Hata: ' + (err.response?.data?.error || err.message));
    }
  };

  // Manuel BH fatura akışı
  const openBhManualDialog = (order) => setBhManualDialog({
    open: true, order, guid: '', invoice_no: '', note: '', busy: false
  });
  const closeBhManualDialog = () => setBhManualDialog({
    open: false, order: null, guid: '', invoice_no: '', note: '', busy: false
  });
  const doBhManual = async () => {
    if (!bhManualDialog.order || bhManualDialog.busy) return;
    const guid = (bhManualDialog.guid || '').trim();
    const inv = (bhManualDialog.invoice_no || '').trim();
    const note = (bhManualDialog.note || '').trim();
    if (!guid && !inv) {
      toast.error('GUID veya fatura no zorunlu');
      return;
    }
    setBhManualDialog(d => ({ ...d, busy: true }));
    try {
      await markBhManual(bhManualDialog.order.id, {
        bh_invoice_guid: guid, bh_invoice_no: inv, note
      });
      toast.success('Manuel BH faturası kaydedildi');
      await load();
      closeBhManualDialog();
    } catch (err) {
      toast.error('Kayıt hatası: ' + (err.response?.data?.error || err.message));
      setBhManualDialog(d => ({ ...d, busy: false }));
    }
  };

  const renderRow = (o) => {
    const statusInfo = STATUS[o.status] || { label: o.status, color: 'default' };
    const bhStatus = o.bh_invoice_status || 'none';
    const bhInfo = BH_STATUS[bhStatus] || BH_STATUS.none;
    const isPendingApproval = ['pending', 'paid_pending_approval'].includes(o.status);
    const isHavalePending = o.status === 'pending' && (o.payment_method || '').toLowerCase() !== 'credit_card';
    const isConfirmedDraftBH = o.status === 'confirmed' && bhStatus === 'draft';
    const isConfirmedFailedBH = o.status === 'confirmed' && bhStatus === 'failed';

    return (
      <TableRow key={o.id} hover sx={isPendingApproval ? { bgcolor: '#fffde7' } : {}}
                selected={selected.has(o.id)}>
        <TableCell padding="checkbox">
          <Checkbox size="small" checked={selected.has(o.id)} onChange={() => toggleSelect(o.id)} />
        </TableCell>
        <TableCell sx={{ fontWeight: 600 }}>#{o.id}</TableCell>
        <TableCell>
          {/* Faz BF: müşteri adı tıklanınca mini-drawer açılır */}
          <Box
            onClick={() => o.user_id && openCustomerDrawer(o.user_id)}
            sx={{ cursor: o.user_id ? 'pointer' : 'default',
                  color: o.user_id ? '#1565C0' : 'inherit',
                  fontWeight: 500,
                  '&:hover': { textDecoration: o.user_id ? 'underline' : 'none' } }}
          >
            {o.user_name || '-'}
          </Box>
          <Typography variant="caption" color="text.secondary">{o.user_company || ''}</Typography>
        </TableCell>
        <TableCell>{o.created_at ? new Date(o.created_at).toLocaleString('tr-TR') : '-'}</TableCell>
        <TableCell sx={{ fontWeight: 600 }}>₺{Number(o.total_price || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</TableCell>
        <TableCell><Chip label={statusInfo.label} color={statusInfo.color} size="small" /></TableCell>
        <TableCell>
          <Stack direction="column" spacing={0.5} alignItems="flex-start">
            <Chip label={bhInfo.label} color={bhInfo.color} size="small" variant="outlined" />
            {o.bh_invoice_guid && (
              <Tooltip title="BH'de görüntüle">
                <Button
                  size="small"
                  startIcon={<OpenInNew fontSize="small" />}
                  onClick={() => window.open(BH_PANEL_INVOICE_VIEW(o.bh_invoice_guid), '_blank')}
                  sx={{ minWidth: 0, p: 0.5, fontSize: 11 }}
                >
                  Aç
                </Button>
              </Tooltip>
            )}
          </Stack>
        </TableCell>
        <TableCell>
          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            {isPendingApproval && (
              <Tooltip title="Onayla — BH'ye TASLAK gider, stok düşer">
                <Button size="small" variant="contained" color="success" startIcon={<Check />}
                  onClick={() => openApproveDialog(o)}>Onayla</Button>
              </Tooltip>
            )}
            {isHavalePending && (
              <Tooltip title="Havale geldi — onay öncesi 'ödeme alındı' işaretle">
                <Button size="small" variant="outlined" color="info" startIcon={<Payment />}
                  onClick={() => doMarkPaid(o)}>Ödeme Aldı</Button>
              </Tooltip>
            )}
            {!['cancelled', 'rejected', 'delivered'].includes(o.status) && (
              <Tooltip title="Reddet — sebebiniz zorunlu">
                <Button size="small" variant="outlined" color="error" startIcon={<Close />}
                  onClick={() => openRejectDialog(o)}>Reddet</Button>
              </Tooltip>
            )}
            {(isConfirmedDraftBH || isConfirmedFailedBH) && (
              <Tooltip title="BH panelinde manuel KESİLMİŞ kes, sonra GUID/no buradan kaydet">
                <Button size="small" variant="outlined" color="warning" startIcon={<Receipt />}
                  onClick={() => openBhManualDialog(o)}>Manuel BH</Button>
              </Tooltip>
            )}
            {/* Faz A: State machine — confirmed sonrası akış (preparing/shipped/delivered) */}
            {['confirmed', 'preparing', 'shipped'].includes(o.status) && (
              <OrderStateActions
                orderId={o.id}
                currentStatus={o.status}
                onChange={load}
                compact
              />
            )}
            <Tooltip title="Detay">
              <IconButton size="small" onClick={() => openDetail(o.id)}><Visibility fontSize="small" /></IconButton>
            </Tooltip>
            {/* Faz BK (2026-05-10): Siparişi kopyala → yeni sipariş prefill */}
            <Tooltip title="↻ Bu siparişi kopyala (yeni sipariş oluştur)">
              <IconButton size="small" onClick={() => openCopyOrder(o.id)} sx={{ color: '#7C3AED' }}>
                <ContentCopy fontSize="small" />
              </IconButton>
            </Tooltip>
            {/* Faz BL (2026-05-10): WhatsApp ile sipariş özetini paylaş */}
            <Tooltip title="💬 WhatsApp ile özet gönder">
              <IconButton size="small" onClick={() => setWaShareOrderId(o.id)} sx={{ color: '#25D366' }}>
                <WhatsAppIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {/* Faz AX (2026-05-09): A5 sipariş etiketi yazdır — auth header için fetch+blob */}
            <Tooltip title="🖨️ Etiket yazdır (A5 / PDF)">
              <IconButton
                size="small"
                sx={{ color: '#1565C0' }}
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('token');
                    const res = await fetch(
                      `https://api.miksanplastik.com.tr/api/admin/orders/${o.id}/print-label`,
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    if (!res.ok) { toast.error('Etiket yüklenemedi: ' + res.status); return; }
                    const html = await res.text();
                    const blob = new Blob([html], { type: 'text/html' });
                    const blobUrl = URL.createObjectURL(blob);
                    window.open(blobUrl, '_blank');
                    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
                  } catch (e) {
                    toast.error('Etiket açılamadı: ' + e.message);
                  }
                }}
              >
                <Print fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={700}>Siparişler</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          {/* Faz AZ: toplu etiket — sadece seçim varken aktif */}
          {selected.size > 0 && (
            <>
              {/* Faz BA: toplu onayla — sadece onay-bekliyor tab'da görünür */}
              {activeTab === 0 && (
                <Button
                  variant="contained"
                  size="small"
                  color="success"
                  startIcon={<Check />}
                  onClick={openBulkApproveDialog}
                >
                  ✓ Toplu Onayla ({selected.size})
                </Button>
              )}
              {/* Faz BC: toplu kargoya ver — sadece Onaylı tab'da görünür */}
              {activeTab === 1 && (
                <Button
                  variant="contained"
                  size="small"
                  color="info"
                  startIcon={<LocalShipping />}
                  onClick={openBulkShipDialog}
                >
                  🚚 Toplu Kargo ({selected.size})
                </Button>
              )}
              <Button
                variant="contained"
                size="small"
                startIcon={<Print />}
                onClick={openBulkPrint}
                sx={{ bgcolor: '#1565C0' }}
              >
                🖨️ Toplu Etiket ({selected.size})
              </Button>
            </>
          )}
          {/* Faz BE: Yeni sipariş — telefonla / yüz yüze alınan B2B sipariş */}
          <Button variant="contained" size="small" color="success"
                  onClick={() => setQuickCreateOpen(true)}>
            + Yeni Sipariş
          </Button>
          <Button variant="outlined" size="small" onClick={load} disabled={loading}>
            {loading ? <CircularProgress size={16} /> : 'Yenile'}
          </Button>
        </Stack>
      </Stack>

      <QuickCreateOrderDialog
        open={quickCreateOpen}
        prefillOrderId={copyOrderId}
        onClose={() => { setQuickCreateOpen(false); setCopyOrderId(null); }}
        onCreated={() => load()}
      />

      {/* Faz BF: Müşteri mini 360 drawer */}
      <CustomerMiniDrawer
        userId={customerDrawer.userId}
        open={customerDrawer.open}
        onClose={() => setCustomerDrawer({ open: false, userId: null })}
      />

      {/* Faz BL: WhatsApp share dialog */}
      <WhatsAppShareDialog
        orderId={waShareOrderId}
        open={!!waShareOrderId}
        onClose={() => setWaShareOrderId(null)}
      />

      {/* Faz 14b sonrasi (2026-04-30): Tab badge yerine inline Chip — Badge component'i
          'right: -16' offset ile Tab overflow'u tarafindan kesilip yarim gosteriliyordu.
          Inline Chip her tarayicida overflow problemine duşmez ve daha okunabilir. */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        {TABS.map((t, i) => (
          <Tab
            key={t.key}
            label={
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                <span>{t.label}</span>
                <Chip
                  label={tabCounts[i] || 0}
                  size="small"
                  color={
                    tabCounts[i] === 0
                      ? 'default'
                      : (t.key === 'pending_approval' ? 'warning' : 'primary')
                  }
                  sx={{
                    height: 20,
                    minWidth: 28,
                    fontSize: 11,
                    fontWeight: 700,
                    '& .MuiChip-label': { px: 1 }
                  }}
                />
              </Box>
            }
          />
        ))}
      </Tabs>

      {activeTab === 0 && tabCounts[0] > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>{tabCounts[0]}</strong> sipariş onayınızı bekliyor. Havale geldiyse "Ödeme Aldı" + "Onayla", kart ödemesi geldiyse direkt "Onayla".
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
      ) : filteredOrders.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>Bu sekmede sipariş yok</Paper>
      ) : (
        <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 280px)' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ bgcolor: '#f5f5f5' }}>
                  <Checkbox
                    size="small"
                    indeterminate={
                      filteredOrders.some(o => selected.has(o.id)) &&
                      !filteredOrders.every(o => selected.has(o.id))
                    }
                    checked={
                      filteredOrders.length > 0 &&
                      filteredOrders.every(o => selected.has(o.id))
                    }
                    onChange={() => toggleSelectAll(filteredOrders)}
                  />
                </TableCell>
                {['#', 'Müşteri', 'Tarih', 'Tutar', 'Durum', 'BH Durum', 'İşlem'].map(h => (
                  <TableCell key={h} sx={{ bgcolor: '#f5f5f5', fontWeight: 700 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredOrders.map(renderRow)}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ===== ONAYLA DIALOG ===== */}
      <Dialog open={approveDialog.open} onClose={closeApproveDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Siparişi Onayla #{approveDialog.order?.id}</DialogTitle>
        <DialogContent dividers>
          {approveDialog.order && (
            <>
              <Typography><strong>Müşteri:</strong> {approveDialog.order.user_name} {approveDialog.order.user_company ? '/ ' + approveDialog.order.user_company : ''}</Typography>
              <Typography><strong>Tutar:</strong> ₺{Number(approveDialog.order.total_price).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</Typography>
              <Typography><strong>Ürün sayısı:</strong> {(approveDialog.order.items || []).length}</Typography>

              {/* Faz BB: Müşteri risk profili — banner + flag list */}
              {riskLoading && <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}><CircularProgress size={14} /> <Typography variant="caption">Müşteri profili yükleniyor...</Typography></Box>}
              {riskProfile && (
                <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f8fafc', borderRadius: 1, border: '1px solid #e2e8f0' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 1 }}>📊 Müşteri Profili</Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5, mt: 1, fontSize: 13 }}>
                    <Box>Lifetime sipariş: <strong>{riskProfile.lifetime_confirmed_count}</strong></Box>
                    <Box>Lifetime ciro: <strong>₺{Number(riskProfile.lifetime_revenue).toLocaleString('tr-TR')}</strong></Box>
                    <Box>Son 90gün: <strong>{riskProfile.orders_90d} sipariş / ₺{Number(riskProfile.revenue_90d).toLocaleString('tr-TR')}</strong></Box>
                    <Box>Ortalama: <strong>₺{Number(riskProfile.avg_order_value).toLocaleString('tr-TR')}</strong></Box>
                    {riskProfile.lifetime_rejected_count > 0 && (
                      <Box sx={{ gridColumn: '1 / -1' }}>İptal/red: <strong>{riskProfile.lifetime_rejected_count}</strong> ({(riskProfile.rejection_rate * 100).toFixed(0)}%)</Box>
                    )}
                  </Box>
                  {riskProfile.flags && riskProfile.flags.map((f, i) => (
                    <Alert key={i} severity={f.level} sx={{ mt: 1, py: 0 }}>{f.text}</Alert>
                  ))}
                </Box>
              )}

              <Alert severity="info" sx={{ mt: 2 }}>
                Onaylanırsa: BH'ye <strong>TASLAK fatura</strong> gönderilir (varsa), stok düşer, müşteri "onaylandı" bildirimi alır.<br />
                KESİLMİŞ fatura BH panelinden manuel kesilmeli (sonra "Manuel BH" butonu ile sisteme bildirin).
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeApproveDialog} disabled={approveDialog.busy}>Vazgeç</Button>
          <Button variant="contained" color="success" onClick={doApprove} disabled={approveDialog.busy} startIcon={approveDialog.busy ? <CircularProgress size={16} /> : <Check />}>
            {approveDialog.busy ? 'Onaylanıyor...' : 'Onayla'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== REDDET DIALOG ===== */}
      <Dialog open={rejectDialog.open} onClose={closeRejectDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Siparişi Reddet #{rejectDialog.order?.id}</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Red Sebebi (zorunlu, en az 10 karakter)"
            multiline
            rows={3}
            fullWidth
            value={rejectDialog.reason}
            onChange={e => setRejectDialog(d => ({ ...d, reason: e.target.value }))}
            helperText={`${(rejectDialog.reason || '').length} / 1000 karakter`}
          />
          {rejectDialog.order?.payment_status === 'paid' && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Ödeme alınmış. Müşteriye PayTR/banka iadesi <strong>BH veya PayTR panelinden manuel</strong> başlatılmalı.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRejectDialog} disabled={rejectDialog.busy}>Vazgeç</Button>
          <Button variant="contained" color="error" onClick={doReject} disabled={rejectDialog.busy} startIcon={rejectDialog.busy ? <CircularProgress size={16} /> : <Close />}>
            {rejectDialog.busy ? 'İşleniyor...' : 'Reddet'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== MANUEL BH FATURA DIALOG ===== */}
      <Dialog open={bhManualDialog.open} onClose={closeBhManualDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Manuel BH Faturası — Sipariş #{bhManualDialog.order?.id}</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Adım 1:</strong> BH paneline gidin, KESİLMİŞ faturayı manuel kesin.<br />
            <strong>Adım 2:</strong> BH'nin verdiği GUID veya fatura no'yu aşağıya girin.
          </Alert>
          <Button
            startIcon={<OpenInNew />}
            onClick={() => window.open(BH_PANEL_INVOICE_NEW, '_blank')}
            sx={{ mb: 2 }}
            variant="outlined"
            fullWidth
          >
            BH Paneli — Yeni Fatura
          </Button>
          <Stack spacing={2}>
            <TextField label="BH Fatura GUID" fullWidth value={bhManualDialog.guid}
              onChange={e => setBhManualDialog(d => ({ ...d, guid: e.target.value }))}
              helperText="BH cevabındaki id alanı" />
            <TextField label="BH Fatura No (opsiyonel)" fullWidth value={bhManualDialog.invoice_no}
              onChange={e => setBhManualDialog(d => ({ ...d, invoice_no: e.target.value }))} />
            <TextField label="Not (opsiyonel)" multiline rows={2} fullWidth value={bhManualDialog.note}
              onChange={e => setBhManualDialog(d => ({ ...d, note: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeBhManualDialog} disabled={bhManualDialog.busy}>Vazgeç</Button>
          <Button variant="contained" onClick={doBhManual} disabled={bhManualDialog.busy} startIcon={bhManualDialog.busy ? <CircularProgress size={16} /> : <Receipt />}>
            {bhManualDialog.busy ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== DETAY MODAL — Faz BM (2026-05-10) PROFESYONEL GÖRSEL ===== */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth
              PaperProps={{ sx: { borderRadius: 2 } }}>
        {/* Renkli header — sipariş id + status + müşteri + tarih tek satırda */}
        {detailOrder && (
          <Box sx={{ p: 2.5, bgcolor: '#1565C0', color: 'white' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.85, letterSpacing: 1, textTransform: 'uppercase' }}>
                  Mik-San Plastik · Sipariş
                </Typography>
                <Typography variant="h4" fontWeight={800} sx={{ lineHeight: 1.1 }}>
                  #{detailOrder.id}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.95 }}>
                  {detailOrder.created_at && new Date(detailOrder.created_at).toLocaleString('tr-TR', {
                    day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
                  })}
                </Typography>
              </Box>
              <Stack direction="column" spacing={0.5} alignItems="flex-end">
                <Chip
                  label={(STATUS[detailOrder.status] || { label: detailOrder.status }).label}
                  sx={{ bgcolor: 'white', color: '#1565C0', fontWeight: 700, fontSize: 13 }}
                />
                <Chip
                  label={(BH_STATUS[detailOrder.bh_invoice_status] || BH_STATUS.none).label}
                  size="small"
                  variant="outlined"
                  sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)', fontSize: 11 }}
                />
              </Stack>
            </Stack>
          </Box>
        )}

        <DialogContent sx={{ p: 0 }}>
          {detailLoading ? (
            <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
          ) : detailOrder ? (
            <Box sx={{ p: 2.5 }}>
              {/* Üstte 3 sütun: Müşteri | Adres | Ödeme/BH */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2, mb: 2.5 }}>
                <DetailCard
                  icon="👤" title="Müşteri"
                  body={
                    <>
                      <Typography fontWeight={600}>
                        {detailOrder.user_company || detailOrder.user_name || '-'}
                      </Typography>
                      {detailOrder.user_company && (
                        <Typography variant="caption" color="text.secondary">{detailOrder.user_name}</Typography>
                      )}
                      {detailOrder.user_email && (
                        <Typography variant="caption" display="block">
                          ✉️ <a href={`mailto:${detailOrder.user_email}`} style={{ color: '#1565C0' }}>{detailOrder.user_email}</a>
                        </Typography>
                      )}
                      {(detailOrder.phone || detailOrder.user_phone) && (
                        <Typography variant="caption" display="block">
                          📞 <a href={`tel:${detailOrder.phone || detailOrder.user_phone}`} style={{ color: '#1565C0' }}>
                            {detailOrder.phone || detailOrder.user_phone}
                          </a>
                        </Typography>
                      )}
                      {detailOrder.tax_no && (
                        <Typography variant="caption" display="block">VKN: {detailOrder.tax_no}</Typography>
                      )}
                    </>
                  }
                />
                <DetailCard
                  icon="📍" title="Teslimat Adresi"
                  body={
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                      {detailOrder.address || detailOrder.delivery_address || detailOrder.user_address || '-'}
                    </Typography>
                  }
                />
                <DetailCard
                  icon="💳" title="Ödeme & Fatura"
                  body={
                    <>
                      <Typography variant="body2">
                        <strong>{detailOrder.payment_method || '-'}</strong> · {detailOrder.payment_status || '-'}
                      </Typography>
                      {detailOrder.bh_invoice_guid && (
                        <Typography variant="caption" display="block" sx={{ wordBreak: 'break-all' }}>
                          GUID: {detailOrder.bh_invoice_guid.slice(0, 18)}…
                        </Typography>
                      )}
                      {detailOrder.bizimhesap_id && (
                        <Typography variant="caption" display="block">Fatura: {detailOrder.bizimhesap_id}</Typography>
                      )}
                      {detailOrder.bh_invoice_guid && (
                        <Button size="small" startIcon={<OpenInNew />}
                                onClick={() => window.open(BH_PANEL_INVOICE_VIEW(detailOrder.bh_invoice_guid), '_blank')}
                                sx={{ p: 0, mt: 0.5, fontSize: 11 }}>
                          BH'de Aç
                        </Button>
                      )}
                    </>
                  }
                />
              </Box>

              {/* Notlar (uyarı bandı) */}
              {detailOrder.note && (
                <Alert severity="info" icon="📝" sx={{ mb: 2 }}>
                  <strong>Müşteri notu:</strong> {detailOrder.note}
                </Alert>
              )}
              {detailOrder.admin_note && (
                <Alert severity="warning" icon="🔒" sx={{ mb: 2 }}>
                  <strong>Admin notu:</strong>
                  <Typography component="span" sx={{ display: 'block', whiteSpace: 'pre-line' }}>
                    {detailOrder.admin_note}
                  </Typography>
                </Alert>
              )}
              {detailOrder.reject_reason && (
                <Alert severity="error" icon="❌" sx={{ mb: 2 }}>
                  <strong>Red sebebi:</strong> {detailOrder.reject_reason}
                </Alert>
              )}

              {/* Ürünler — profesyonel tablo */}
              <Typography variant="overline" sx={{ color: '#475569', fontWeight: 700, letterSpacing: 1.5 }}>
                📦 Ürünler ({(detailOrder.items || []).length} kalem)
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mt: 0.5 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                      <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: '#475569' }}>Ürün</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: '#475569', width: 90 }}>Adet</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: '#475569', width: 110 }}>Birim ₺</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: '#475569', width: 120 }}>Toplam</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(detailOrder.items || []).map((it, idx) => (
                      <TableRow key={it.id || idx} hover>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            {it.image_url && (
                              <Box component="img" src={it.image_url} alt={it.name}
                                sx={{ width: 32, height: 32, borderRadius: 0.5, objectFit: 'cover',
                                      border: '1px solid #e2e8f0' }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            )}
                            <Typography fontSize={13} fontWeight={500}>{it.name}</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {it.quantity} <Typography variant="caption" color="text.secondary">{it.unit || 'ad'}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          ₺{Number(it.unit_price).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: '#1565C0' }}>
                          ₺{(Number(it.quantity) * Number(it.unit_price)).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Toplam satırı */}
                    <TableRow sx={{ bgcolor: '#1565C0' }}>
                      <TableCell colSpan={3} align="right" sx={{ color: 'white', fontWeight: 700, fontSize: 14, border: 'none' }}>
                        TOPLAM
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'white', fontWeight: 800, fontSize: 18, border: 'none' }}>
                        ₺{Number(detailOrder.total_price || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : (
            <Box p={3}><Typography>Bulunamadı</Typography></Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5, bgcolor: '#f8fafc' }}>
          {detailOrder && (
            <>
              <Button startIcon={<HistoryIcon />} onClick={() => openHistory(detailOrder.id)} size="small">
                📋 Tam Geçmiş
              </Button>
              <Button startIcon={<Print />} size="small" sx={{ color: '#1565C0' }}
                      onClick={async () => {
                        const token = localStorage.getItem('token');
                        const res = await fetch(`https://api.miksanplastik.com.tr/api/admin/orders/${detailOrder.id}/print-label`,
                                                { headers: { Authorization: `Bearer ${token}` } });
                        if (res.ok) {
                          const blob = new Blob([await res.text()], { type: 'text/html' });
                          window.open(URL.createObjectURL(blob), '_blank');
                        }
                      }}>
                🖨️ Etiket
              </Button>
              <Button startIcon={<WhatsAppIcon />} size="small" sx={{ color: '#25D366' }}
                      onClick={() => { setDetailOpen(false); setWaShareOrderId(detailOrder.id); }}>
                WA Gönder
              </Button>
            </>
          )}
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setDetailOpen(false)} variant="outlined" size="small">Kapat</Button>
        </DialogActions>
      </Dialog>

      {/* ===== NOTE 6 — TAM GEÇMİŞ MODAL ===== */}
      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          📋 Tam Geçmiş — Sipariş #{historyData?.order?.id || '...'}
        </DialogTitle>
        <DialogContent dividers>
          {historyLoading ? (
            <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>
          ) : historyData ? (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip label={`Audit: ${historyData.counts.audit}`} size="small" />
                <Chip label={`Push: ${historyData.counts.push}`} size="small" color="secondary" />
                <Chip label={`Kargo: ${historyData.counts.shipments}`} size="small" color="info" />
                <Chip label={`E-Fatura: ${historyData.counts.efatura}`} size="small" color="success" />
              </Stack>
              <Divider />
              {historyData.timeline.length === 0 ? (
                <Typography color="text.secondary">Henüz hiç olay yok</Typography>
              ) : (
                <Box>
                  {historyData.timeline.map((ev, i) => {
                    const style = EVENT_STYLE[ev.kind] || { color: '#888', icon: '•' };
                    const dt = ev.at ? new Date(ev.at) : null;
                    return (
                      <Box key={i} sx={{
                        display: 'flex', gap: 2, py: 1.2,
                        borderBottom: i < historyData.timeline.length - 1 ? '1px solid #eee' : 'none'
                      }}>
                        <Box sx={{ minWidth: 28, fontSize: 18 }}>{style.icon}</Box>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography sx={{ fontWeight: 600, color: style.color, fontSize: 14 }}>
                            {ev.label}
                          </Typography>
                          {ev.meta && Object.keys(ev.meta).length > 0 && (
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', wordBreak: 'break-word' }}>
                              {Object.entries(ev.meta)
                                .filter(([k, v]) => v !== null && v !== undefined && v !== '' && !['id', 'created_at', 'updated_at'].includes(k))
                                .slice(0, 5)
                                .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v).slice(0, 80) : String(v).slice(0, 80)}`)
                                .join(' · ')}
                            </Typography>
                          )}
                        </Box>
                        <Typography variant="caption" sx={{ minWidth: 130, textAlign: 'right', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                          {dt ? dt.toLocaleString('tr-TR') : '-'}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}
              {historyData.shipments.length > 0 && (
                <>
                  <Divider />
                  <Typography variant="subtitle2">🚚 Kargo Kayıtları</Typography>
                  {historyData.shipments.map(s => (
                    <Paper key={s.id} sx={{ p: 1.5, bgcolor: '#F8FAFC' }}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Chip label={s.carrier?.toUpperCase()} size="small" color="info" />
                        <Typography sx={{ fontFamily: 'monospace', fontSize: 13 }}>{s.tracking_no}</Typography>
                        <Chip label={s.status} size="small" />
                        {s.is_mock ? <Chip label="MOCK" size="small" color="warning" sx={{ fontSize: 10 }} /> : null}
                      </Stack>
                    </Paper>
                  ))}
                </>
              )}
              {historyData.efatura.length > 0 && (
                <>
                  <Divider />
                  <Typography variant="subtitle2">🧾 E-Fatura Kayıtları</Typography>
                  {historyData.efatura.map(e => (
                    <Paper key={e.id} sx={{ p: 1.5, bgcolor: '#F0FDF4' }}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Chip label={e.provider?.toUpperCase()} size="small" color="success" />
                        <Typography sx={{ fontFamily: 'monospace', fontSize: 13 }}>{e.invoice_no}</Typography>
                        <Chip label={e.status} size="small" />
                        {e.is_mock ? <Chip label="MOCK" size="small" color="warning" sx={{ fontSize: 10 }} /> : null}
                      </Stack>
                    </Paper>
                  ))}
                </>
              )}
            </Stack>
          ) : (
            <Typography>Veri yok</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>Kapat</Button>
        </DialogActions>
      </Dialog>

      {/* ===== FAZ BC: TOPLU KARGOYA VER DIALOG ===== */}
      <Dialog open={bulkShipDialog.open}
              onClose={() => !bulkShipDialog.busy && setBulkShipDialog({ open: false, busy: false, results: null, carrier: 'aras', notes: '' })}
              maxWidth="sm" fullWidth>
        <DialogTitle>
          {bulkShipDialog.results ? 'Toplu Kargo Sonucu' : `Toplu Kargoya Ver — ${bulkShipDialog.shippableIds?.length || 0} sipariş`}
        </DialogTitle>
        <DialogContent dividers>
          {!bulkShipDialog.results ? (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Seçili <strong>{bulkShipDialog.shippableIds?.length}</strong> sipariş kargoya verilecek.
                Mock mode'da fake AWB üretilir; gerçek API entegrasyonu için credentials yüklenmesi gerekir.
              </Alert>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Kargo Firması</InputLabel>
                <Select
                  value={bulkShipDialog.carrier}
                  label="Kargo Firması"
                  onChange={(e) => setBulkShipDialog(d => ({ ...d, carrier: e.target.value }))}
                >
                  <MenuItem value="aras">Aras Kargo</MenuItem>
                  <MenuItem value="mng">MNG Kargo</MenuItem>
                  <MenuItem value="yurtici">Yurtiçi Kargo</MenuItem>
                  <MenuItem value="ptt">PTT</MenuItem>
                  <MenuItem value="manual">Manuel (kargo no sonra girilir)</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth size="small" label="Not (opsiyonel — tüm gönderilere uygulanır)"
                value={bulkShipDialog.notes}
                onChange={(e) => setBulkShipDialog(d => ({ ...d, notes: e.target.value }))}
                multiline rows={2}
              />
            </>
          ) : (
            <Box>
              <Stack direction="row" spacing={2} mb={2}>
                <Chip label={`${bulkShipDialog.results.success_count} başarılı`} color="success" />
                <Chip label={`${bulkShipDialog.results.fail_count} başarısız`} color="error" />
                {bulkShipDialog.results.is_mock && <Chip label="Mock AWB (credentials yok)" color="warning" variant="outlined" />}
              </Stack>
              <List dense sx={{ maxHeight: 320, overflow: 'auto' }}>
                {bulkShipDialog.results.results.map(r => (
                  <ListItem key={r.order_id} sx={{ borderLeft: '3px solid', borderColor: r.success ? 'success.main' : 'error.main' }}>
                    <ListItemText
                      primary={`#${r.order_id} — ${r.success ? `Kargoda — ${r.tracking_no}` : 'Başarısız'}`}
                      secondary={r.success ? `${r.carrier.toUpperCase()}` : r.error}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {!bulkShipDialog.results ? (
            <>
              <Button onClick={() => setBulkShipDialog({ open: false, busy: false, results: null, carrier: 'aras', notes: '' })} disabled={bulkShipDialog.busy}>Vazgeç</Button>
              <Button variant="contained" color="info" onClick={doBulkShip} disabled={bulkShipDialog.busy} startIcon={bulkShipDialog.busy ? <CircularProgress size={16} /> : <LocalShipping />}>
                {bulkShipDialog.busy ? 'Gönderiliyor...' : `${bulkShipDialog.shippableIds?.length} Siparişi Kargoya Ver`}
              </Button>
            </>
          ) : (
            <Button onClick={() => setBulkShipDialog({ open: false, busy: false, results: null, carrier: 'aras', notes: '' })}>Kapat</Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ===== FAZ BA: TOPLU ONAYLAMA DIALOG ===== */}
      <Dialog open={bulkApproveDialog.open}
              onClose={() => !bulkApproveDialog.busy && setBulkApproveDialog({ open: false, busy: false, results: null })}
              maxWidth="sm" fullWidth>
        <DialogTitle>
          {bulkApproveDialog.results ? 'Toplu Onay Sonucu' : `Toplu Onayla — ${bulkApproveDialog.approvableIds?.length || 0} sipariş`}
        </DialogTitle>
        <DialogContent dividers>
          {!bulkApproveDialog.results ? (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Seçili <strong>{bulkApproveDialog.approvableIds?.length}</strong> sipariş onaylanacak.
                Her biri sırayla işlenir (BH rate limit). Bir sipariş başarısız olursa diğerleri devam eder.
              </Alert>
              <Alert severity="warning">
                BH'ye TASLAK fatura gider, stok düşer, müşteri "onaylandı" bildirimi alır.
                BH hatası olursa lokal akış tamamlanır, BH manuel kesilebilir.
              </Alert>
            </>
          ) : (
            <Box>
              <Stack direction="row" spacing={2} mb={2}>
                <Chip label={`${bulkApproveDialog.results.success_count} başarılı`} color="success" />
                <Chip label={`${bulkApproveDialog.results.fail_count} başarısız`} color="error" />
                {bulkApproveDialog.results.bh_failed_count > 0 && (
                  <Chip label={`${bulkApproveDialog.results.bh_failed_count} BH hata (lokal OK)`} color="warning" />
                )}
              </Stack>
              <List dense sx={{ maxHeight: 320, overflow: 'auto' }}>
                {bulkApproveDialog.results.results.map(r => (
                  <ListItem key={r.order_id} sx={{ borderLeft: '3px solid', borderColor: r.success ? (r.bh_failed ? 'warning.main' : 'success.main') : 'error.main' }}>
                    <ListItemText
                      primary={`#${r.order_id} — ${r.success ? (r.bh_failed ? 'Onaylandı (BH hata)' : 'Onaylandı') : 'Başarısız'}`}
                      secondary={r.success ? (r.bh_failed ? r.bh_error : r.bh_invoice_guid ? `BH GUID: ${r.bh_invoice_guid}` : '') : r.error}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {!bulkApproveDialog.results ? (
            <>
              <Button onClick={() => setBulkApproveDialog({ open: false, busy: false, results: null })} disabled={bulkApproveDialog.busy}>Vazgeç</Button>
              <Button variant="contained" color="success" onClick={doBulkApprove} disabled={bulkApproveDialog.busy} startIcon={bulkApproveDialog.busy ? <CircularProgress size={16} /> : <Check />}>
                {bulkApproveDialog.busy ? 'Onaylanıyor...' : `${bulkApproveDialog.approvableIds?.length} Siparişi Onayla`}
              </Button>
            </>
          ) : (
            <Button onClick={() => setBulkApproveDialog({ open: false, busy: false, results: null })}>Kapat</Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
